import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { documentoAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { FileText, Search, Filter, Plus, Eye, RefreshCw } from 'lucide-react';

export default function MisGuiasPage() {
    const [documentos, setDocumentos] = useState([]);
    const [filteredDocs, setFilteredDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('todos');

    useEffect(() => {
        loadDocumentos();
    }, []);

    useEffect(() => {
        filterDocuments();
    }, [documentos, searchTerm, estadoFilter]);

    const loadDocumentos = async () => {
        setLoading(true);
        try {
            const response = await documentoAPI.getAll();
            setDocumentos(response.data);
        } catch (err) {
            console.error('Error loading documentos:', err);
        } finally {
            setLoading(false);
        }
    };

    const filterDocuments = () => {
        let filtered = [...documentos];

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (doc) =>
                    (doc.folio || '').toLowerCase().includes(term) ||
                    (doc.folio_fisico || '').toLowerCase().includes(term) ||
                    (doc.patente_tracto || doc.patente || '').toLowerCase().includes(term) ||
                    (doc.patente_semiremolque || '').toLowerCase().includes(term) ||
                    (doc.nombre_conductor || '').toLowerCase().includes(term) ||
                    (doc.rut_conductor || '').toLowerCase().includes(term) ||
                    (doc.razon_social || '').toLowerCase().includes(term) ||
                    (doc.rut_empresa || doc.rut_destino || '').toLowerCase().includes(term)
            );
        }

        // Filter by estado
        if (estadoFilter !== 'todos') {
            filtered = filtered.filter((doc) => doc.estado === estadoFilter);
        }

        setFilteredDocs(filtered);
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Mis Guías
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {filteredDocs.length} guía{filteredDocs.length !== 1 ? 's' : ''} encontrada{filteredDocs.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={loadDocumentos}
                            className="border-2"
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Actualizar
                        </Button>
                        <Link to="/guias/crear">
                            <Button className="btn-industrial bg-primary text-primary-foreground" data-testid="new-guide-btn">
                                <Plus className="h-5 w-5 mr-2" />
                                Nueva Guía
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Filters */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar por folio interno, N° guía/factura, patente, conductor, RUT o razón social..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 input-industrial"
                                    data-testid="search-input"
                                />
                            </div>
                            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                                <SelectTrigger className="w-full md:w-48 input-industrial" data-testid="estado-filter">
                                    <Filter className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder="Estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="todos">Todos los estados</SelectItem>
                                    <SelectItem value="pendiente">Pendiente</SelectItem>
                                    <SelectItem value="autorizado">Autorizado</SelectItem>
                                    <SelectItem value="despachado">Despachado</SelectItem>
                                    <SelectItem value="rechazado">Rechazado</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="border-2">
                    <ScrollArea className="w-full">
                        <Table className="table-industrial">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Folio</TableHead>
                                    <TableHead>N° guía/factura</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Patente</TableHead>
                                    <TableHead>Conductor</TableHead>
                                    <TableHead>Destino</TableHead>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <TableRow key={i}>
                                                <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                                <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                                            </TableRow>
                                        ))}
                                    </>
                                ) : filteredDocs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            <div className="empty-state py-12">
                                                <FileText className="empty-state-icon" />
                                                <p className="empty-state-title">Sin guías</p>
                                                <p className="empty-state-description">
                                                    {searchTerm || estadoFilter !== 'todos'
                                                        ? 'No se encontraron guías con los filtros aplicados'
                                                        : 'Crea tu primera guía de despacho'}
                                                </p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredDocs.map((doc) => (
                                        <TableRow key={doc.id} data-testid={`row-${doc.folio}`}>
                                            <TableCell>
                                                <span className="folio-display">{doc.folio}</span>
                                            </TableCell>
                                            <TableCell className="font-mono font-bold">
                                                {doc.folio_fisico || '—'}
                                            </TableCell>
                                            <TableCell className="capitalize">{doc.tipo_documento_nombre || doc.tipo}</TableCell>
                                            <TableCell className="font-mono">{doc.patente_tracto || doc.patente}</TableCell>
                                            <TableCell className="text-sm">
                                                {doc.nombre_conductor || '—'}
                                                {doc.rut_conductor && (
                                                    <div className="text-xs text-muted-foreground font-mono">{doc.rut_conductor}</div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {doc.razon_social || doc.destino_info?.razon_social || doc.rut_empresa || doc.rut_destino}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                                {formatDate(doc.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge estado={doc.estado} size="sm" />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Link to={`/guias/documento/${doc.id}`}>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        data-testid={`view-${doc.folio}`}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                </Card>
            </div>
        </Layout>
    );
}
